const SiteHeader = {
  controller: null,

  init() {
    if (this.controller) return

    const header = document.querySelector('[data-site-header]')
    if (!header) return

    const toggle = header.querySelector('[data-navigation-toggle]')
    const navigation = header.querySelector('[data-navigation]')

    this.controller = new AbortController()
    const { signal } = this.controller
    const mobile = window.matchMedia('(width <= 1199px)')
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    const adminBar = document.getElementById('wpadminbar')
    const content = document.getElementById('content')
    let headerHeight = header.offsetHeight
    let previousY = Math.max(0, window.scrollY)
    let direction = 0
    let distance = 0
    let frame = null
    const isNearTop = y => y <= headerHeight

    const show = () => header.classList.remove('is-hidden')
    const hide = () => {
      header.classList.add('is-hidden')
      if (reducedMotion.matches) header.classList.add('is-scrolled')
    }

    const setOpen = open => {
      if (!toggle || !navigation) return
      const isOpen = mobile.matches && open
      toggle.setAttribute('aria-expanded', String(isOpen))
      toggle.setAttribute('aria-label', isOpen ? toggle.dataset.closeLabel : toggle.dataset.openLabel)
      navigation.setAttribute('aria-hidden', String(mobile.matches && !isOpen))
      navigation.inert = mobile.matches && !isOpen
      header.classList.toggle('is-menu-open', isOpen)
      document.documentElement.classList.toggle('has-open-navigation', isOpen)
      if (isOpen) show()
    }

    if (toggle) {
      toggle.hidden = false
      header.classList.add('has-navigation-toggle')
    }
    setOpen(false)

    const resize = () => {
      headerHeight = header.offsetHeight
      content?.style.setProperty('--site-header-height', `${headerHeight}px`)
      if (adminBar) header.style.setProperty('--site-header-top', `${Math.max(0, adminBar.getBoundingClientRect().bottom)}px`)
    }

    header.classList.toggle('is-scrolled', !isNearTop(previousY))
    resize()
    header.classList.add('is-fixed')
    const observer = new ResizeObserver(resize)
    observer.observe(header)
    if (adminBar) observer.observe(adminBar)

    const update = () => {
      frame = null
      const y = Math.max(0, Math.min(window.scrollY, document.documentElement.scrollHeight - window.innerHeight))
      const delta = y - previousY
      if (isNearTop(y)) header.classList.remove('is-scrolled')
      const nextDirection = Math.sign(delta)
      if (nextDirection && nextDirection !== direction) distance = 0
      distance += Math.abs(delta)
      if (nextDirection) direction = nextDirection
      previousY = y

      if (adminBar) header.style.setProperty('--site-header-top', `${Math.max(0, adminBar.getBoundingClientRect().bottom)}px`)

      if (
        isNearTop(y) ||
        header.matches(':focus-within') ||
        header.querySelector('details[open]') ||
        (mobile.matches && toggle?.getAttribute('aria-expanded') === 'true')
      ) {
        show()
        distance = 0
        return
      }

      // Ignore small trackpad movements and momentum settling at direction changes.
      if (distance >= 8) {
        if (direction > 0) hide()
        else show()
        distance = 0
      }
    }

    window.addEventListener(
      'scroll',
      () => {
        if (frame === null) frame = window.requestAnimationFrame(update)
      },
      { passive: true, signal }
    )
    window.addEventListener('resize', resize, { signal })
    header.addEventListener('focusin', show, { signal })
    header.addEventListener(
      'transitionend',
      event => {
        if (event.target !== header || event.propertyName !== 'transform' || !header.classList.contains('is-hidden')) return
        header.classList.add('is-scrolled')
      },
      { signal }
    )
    toggle?.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'), { signal })
    mobile.addEventListener('change', () => setOpen(false), { signal })
    navigation?.addEventListener(
      'click',
      event => {
        if (mobile.matches && event.target.closest('a')) setOpen(false)
      },
      { signal }
    )
    header.addEventListener(
      'keydown',
      event => {
        if (event.key !== 'Escape' || !mobile.matches || !toggle || toggle.getAttribute('aria-expanded') !== 'true') return
        toggle.focus()
        setOpen(false)
      },
      { signal }
    )

    this.cleanup = () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      observer.disconnect()
      content?.style.removeProperty('--site-header-height')
      header.classList.remove('has-navigation-toggle', 'is-fixed', 'is-hidden', 'is-menu-open', 'is-scrolled')
      header.style.removeProperty('--site-header-top')
      document.documentElement.classList.remove('has-open-navigation')
      if (toggle) {
        toggle.hidden = true
        toggle.setAttribute('aria-expanded', 'false')
      }
      if (navigation) {
        navigation.removeAttribute('aria-hidden')
        navigation.inert = false
      }
    }
  },

  destroy() {
    this.controller?.abort()
    this.controller = null
    this.cleanup?.()
    this.cleanup = null
  }
}

export default SiteHeader
